/**
 * Byrå service — thin Axios wrapper for the accounting-firm (byrå) tier
 * endpoints (design §4.8b, Phase 4 item 4): the cross-client exception queue
 * and per-client consent.
 *
 * Mirrors MatchingService.js / InsightsService.js: pure functions that take a
 * companyId + payload and return axios promises. Callers own loading state and
 * errors — the feature is gated ('byra_tier') and may 403/404, so every caller
 * must render an off-state rather than crash.
 *
 * Contract (backend byraGlobalRoutes.js →
 *   /:companyId/accounting/reconciliation/byra):
 *   GET  /queue?scope=firm → { success, scope, clients: [{ company_id,
 *          company_name, bank:{unmatched,auto_matched}, tax_authority:{...},
 *          open_total, drilldown:[...] }], totals:{clients,
 *          clients_with_backlog, open_total} }
 *   GET  /consent          → { success, consent: [...] }
 *   POST /consent          → { success, ... }  Body: { firmId, queueEnabled?,
 *          precedentEnabled?, rulesEnabled? }
 */

import axios from './AxiosInstance';
import { getApiUrl } from '../utils/ApiUtils';

const config = { withCredentials: true };

const base = (companyId) => `${companyId}/accounting/reconciliation/byra`;

// ---------------------------------------------------------------------------
// Pure helpers (unit tested)
// ---------------------------------------------------------------------------

/** Swedish integer formatter (thousands grouping). */
const nf = new Intl.NumberFormat('sv-SE');

/** Format an integer count for the byrå tables ('1234' → '1 234'). */
export function formatCount(value) {
    const v = parseInt(value, 10);
    return Number.isFinite(v) ? nf.format(v) : '0';
}

/**
 * Open-row counts for one client row → { bank, tax, total }. Open = the two
 * review-tier statuses the backend aggregates (unmatched + auto_matched, the
 * lines still needing firm attention). Defensive against partial payloads.
 */
export function openRowCounts(client = {}) {
    const bankBucket = client.bank || {};
    const taxBucket = client.tax_authority || {};
    const bank = (parseInt(bankBucket.unmatched, 10) || 0) + (parseInt(bankBucket.auto_matched, 10) || 0);
    const tax = (parseInt(taxBucket.unmatched, 10) || 0) + (parseInt(taxBucket.auto_matched, 10) || 0);
    // Prefer the backend's own open_total when present; fall back to our sum.
    const total = Number.isFinite(parseInt(client.open_total, 10))
        ? parseInt(client.open_total, 10)
        : bank + tax;
    return { bank, tax, total };
}

/** True when a client still has open review-tier rows (backlog). */
export function clientHasBacklog(client = {}) {
    return openRowCounts(client).total > 0;
}

/**
 * Totals header row, defensively: prefer the backend's `totals`, fall back to
 * recomputing from the clients array so the header is never blank when an older
 * backend omits totals. Returns { clients, clients_with_backlog, open_total }.
 */
export function byraTotals(queue = {}) {
    const t = queue.totals;
    if (t && typeof t === 'object') {
        return {
            clients: parseInt(t.clients, 10) || 0,
            clients_with_backlog: parseInt(t.clients_with_backlog, 10) || 0,
            open_total: parseInt(t.open_total, 10) || 0,
        };
    }
    const clients = Array.isArray(queue.clients) ? queue.clients : [];
    return clients.reduce((acc, c) => {
        const { total } = openRowCounts(c);
        acc.clients += 1;
        acc.clients_with_backlog += total > 0 ? 1 : 0;
        acc.open_total += total;
        return acc;
    }, { clients: 0, clients_with_backlog: 0, open_total: 0 });
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

/**
 * GET /byra/queue?scope=firm
 * The cross-client exception queue for the authenticated byrå user. Returns
 * the response body as-is ({ success, scope, clients, totals }).
 */
export async function getByraQueue(companyId, { scope = 'firm' } = {}) {
    const url = getApiUrl(`${base(companyId)}/queue`);
    const { data } = await axios.get(url, { ...config, params: { scope } });
    return data;
}

/**
 * GET /byra/consent — consent rows for the CURRENT client company.
 * Returns { success, consent: [...] }.
 */
export async function getByraConsent(companyId) {
    const url = getApiUrl(`${base(companyId)}/consent`);
    const { data } = await axios.get(url, config);
    return data;
}

/**
 * POST /byra/consent — set the current client's consent to a firm.
 * Body: { firmId, queueEnabled?, precedentEnabled?, rulesEnabled? }.
 */
export async function setByraConsent(companyId, { firmId, queueEnabled, precedentEnabled, rulesEnabled } = {}) {
    const url = getApiUrl(`${base(companyId)}/consent`);
    const body = {
        firmId,
        ...(queueEnabled !== undefined ? { queueEnabled } : {}),
        ...(precedentEnabled !== undefined ? { precedentEnabled } : {}),
        ...(rulesEnabled !== undefined ? { rulesEnabled } : {}),
    };
    const { data } = await axios.post(url, body, config);
    return data;
}
